import { Fragment, lazy, Suspense, useEffect, useRef, useState } from "react"
import "./styles.css"
import type { StageProps } from "@site-types/components/stage"

const StageScene = lazy(() => import("./StageScene"))

export function Stage({ graphicText = "PLAYBRAIN SHARES GAME CULTURE" }: StageProps) {
  const words = graphicText.split(" ").filter(Boolean)
  const holderRef = useRef<HTMLDivElement | null>(null)
  const [isSceneIdle, setIsSceneIdle] = useState(false)
  const [isSceneVisible, setIsSceneVisible] = useState(false)
  const shouldMountScene = isSceneIdle && isSceneVisible

  useEffect(() => {
    if (typeof window === "undefined") return

    const requestIdle =
      window.requestIdleCallback ??
      ((callback: IdleRequestCallback) =>
        window.setTimeout(() => callback({ didTimeout: false, timeRemaining: () => 0 }), 800))
    const cancelIdle = window.cancelIdleCallback ?? window.clearTimeout
    const idleId = requestIdle(() => setIsSceneIdle(true), { timeout: 1600 })

    return () => cancelIdle(idleId)
  }, [])

  useEffect(() => {
    const holder = holderRef.current
    if (!holder || typeof window === "undefined") return

    if (!("IntersectionObserver" in window)) {
      setIsSceneVisible(true)
      return
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return
        setIsSceneVisible(true)
        observer.disconnect()
      },
      { rootMargin: "240px" }
    )
    observer.observe(holder)
    return () => observer.disconnect()
  }, [])

  return (
    <>
      <h1 className="stage-heading">
        <StageHeadingWords words={words} />
      </h1>

      <div
        ref={holderRef}
        className="stage-holder"
      >
        <Suspense
          fallback={
            <div
              className="stage-scene-fallback"
              aria-hidden="true"
            />
          }
        >
          {shouldMountScene ? <StageScene /> : null}
        </Suspense>
      </div>

      <div
        aria-hidden="true"
        className="stage-heading over"
      >
        <StageHeadingWords words={words} />
      </div>
    </>
  )
}

function StageHeadingWords({ words }: { words: string[] }) {
  return words.map((word, index) => (
    <Fragment key={`${word}-${index}`}>
      <span>{word}</span>
      {index < words.length - 1 ? " " : null}
    </Fragment>
  ))
}

export default Stage
